<template>
  <section
    class="ui-panel"
    :class="{ 'ui-panel--raised': raised, 'ui-panel--etched': etched }"
    :data-tone="tone ?? undefined"
  >
    <slot />
  </section>
</template>

<script setup lang="ts">
withDefaults(
  defineProps<{
    raised?: boolean;
    etched?: boolean;
    tone?: "travel" | "work" | "combat" | "econ" | "social" | null;
  }>(),
  {
    raised: false,
    etched: true,
    tone: null
  }
);
</script>

<style scoped>
.ui-panel {
  position: relative;
  background: color-mix(in srgb, var(--bg-1) 92%, transparent);
  border: 1px solid var(--border-0);
  border-radius: var(--radius-2);
  padding: var(--space-3);
  overflow: hidden;
  min-width: 0;
}

.ui-panel--raised {
  background: color-mix(in srgb, var(--bg-2) 94%, transparent);
  border-color: var(--border-1);
  box-shadow: var(--shadow-1);
}

.ui-panel--etched::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-radius: inherit;
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--accent) 12%, transparent), transparent 38%),
    radial-gradient(circle at 12% 18%, color-mix(in srgb, var(--accent) 10%, transparent), transparent 52%);
  opacity: 0.9;
}

.ui-panel--etched::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-radius: inherit;
  --tick: color-mix(in srgb, var(--accent) 16%, var(--border-1));
  background:
    linear-gradient(var(--tick), var(--tick)) left top / 18px 1px no-repeat,
    linear-gradient(var(--tick), var(--tick)) left top / 1px 18px no-repeat,
    linear-gradient(var(--tick), var(--tick)) right top / 18px 1px no-repeat,
    linear-gradient(var(--tick), var(--tick)) right top / 1px 18px no-repeat,
    linear-gradient(var(--tick), var(--tick)) left bottom / 18px 1px no-repeat,
    linear-gradient(var(--tick), var(--tick)) left bottom / 1px 18px no-repeat,
    linear-gradient(var(--tick), var(--tick)) right bottom / 18px 1px no-repeat,
    linear-gradient(var(--tick), var(--tick)) right bottom / 1px 18px no-repeat;
  opacity: 0.75;
}

.ui-panel[data-tone="travel"] {
  --accent: var(--accent-travel);
}

.ui-panel[data-tone="work"] {
  --accent: var(--accent-work);
}

.ui-panel[data-tone="combat"] {
  --accent: var(--accent-combat);
}

.ui-panel[data-tone="econ"] {
  --accent: var(--accent-econ);
}

.ui-panel[data-tone="social"] {
  --accent: var(--accent-social);
}
</style>
